import bugsnagClient from 'JS/global/bugsnag';
import Component from 'vue-class-component';
import WithUniqueId from 'JS/mixins/WithUniqueId';
import { IAutoPartsVehicle, IAutoPartsVehicleCombinedMake, IAutoPartsVehicleEngine, IAutoPartsVehicleModel } from 'JS/types/Automotive';
import { jsonEquals } from 'JS/helpers/functions';
import { Mixins, Prop, Watch } from 'vue-property-decorator';
import { VehicleLookupService } from 'JS/services/automotive/VehicleLookupService';
import { VehicleModelData } from '../types/autoPartsVehicleLookup';

@Component
export default class AutoPartsVehicleLookupBase extends Mixins(WithUniqueId) {

	@Prop({ type: Object, required: false })
	readonly value: IAutoPartsVehicle;

	/* Computed
	============================================*/

	get vehicle(): IAutoPartsVehicle | null {
		if (!this.year || !this.model || !this.engine) return null;
		return {
			year: this.year,
			makeID: this.modelData.makeID, // Makes can have a second ID for trucks. Always use the makeID of the model
			modelID: this.modelData.modelID,
			isTruck: this.model.isTruck,
			engineID: this.engine.engineID,
			makeDescription: this.make.description,
			modelDescription: this.model.description,
			engineDescription: this.engine.description
		};
	}

	get make(): IAutoPartsVehicleCombinedMake | null {
		if (!this.makeID) return null;
		return this.makes?.find(x => x.makeID == this.makeID || x.truckMakeID == this.makeID) ?? null;
	}

	get model(): IAutoPartsVehicleModel | null {
		if (!this.modelData) return null;

		return this.models?.find(x => {
			return x.year == this.year
				&& x.makeID == this.modelData.makeID
				&& x.modelID == this.modelData.modelID;
		}) ?? null;
	}

	get engine(): IAutoPartsVehicleEngine | null {
		if (!this.engineID || !this.model) return null;
		return this.engines?.find(x => {
			return x.year == this.year
				&& x.makeID == this.modelData.makeID
				&& x.modelID == this.modelData.modelID
				&& x.engineID == this.engineID;
		}) ?? null;
	}

	/* Data
	============================================*/

	year: number | null = null;
	makeID: number | null = null;
	modelData: VehicleModelData | null = null;
	engineID: number | null = null;

	years: number[] = [];
	makes: IAutoPartsVehicleCombinedMake[] = [];
	models: IAutoPartsVehicleModel[] = [];
	engines: IAutoPartsVehicleEngine[] = [];
	service: VehicleLookupService;
	loadingData: boolean = false;

	/* Methods
	============================================*/

	async loadYears(): Promise<void> {
		this.loadingData = true;
		try {
			this.years = await this.service.getAutoPartYears();
		} catch (e) {
			bugsnagClient.notify(e);
		} finally {
			this.loadingData = false;
		}
	}

	async loadMakes(): Promise<void> {
		if (!this.year) return;
		this.loadingData = true;
		try {
			this.makes = await this.service.getAutoPartMakes(this.year);
		} catch (e) {
			bugsnagClient.notify(e);
		} finally {
			this.loadingData = false;
		}
	}

	async loadModels(): Promise<void> {
		if (!this.make) return;
		this.loadingData = true;
		try {
			this.models = await this.service.getAutoPartModels(this.year, this.makeID, this.make.truckMakeID);
		} catch (e) {
			bugsnagClient.notify(e);
		} finally {
			this.loadingData = false;
		}
	}

	async loadEngines(): Promise<void> {
		this.loadingData = true;
		try {
			await this.$nextTick();
			this.engines = await this.service.getAutoPartEngines(this.year, this.modelData.makeID, this.modelData.modelID);
		} catch (e) {
			bugsnagClient.notify(e);
		} finally {
			this.loadingData = false;
		}
	}

	setVehicle(value: IAutoPartsVehicle | null): void {
		// ModelID is required to compute the correct makeID
		if (!value?.modelID || jsonEquals(value, this.vehicle)) return;

		this.year = value.year;
		this.modelData.modelID = value.modelID;
		this.engineID = value.engineID;
	}

	/* Lifecycle Hooks
	============================================*/

	created(): void {
		this.service = new VehicleLookupService();
		this.loadYears();
	}

	beforeMount(): void {
		this.setVehicle(this.value);
	}

	/* Watchers
	============================================*/

	@Watch('year')
	yearChanged(): void {
		if (!this.year) return;
		this.makeID = null;
		this.modelData = null;
		this.engineID = null;
		this.loadMakes();
	}

	@Watch('makeID')
	makeChanged(): void {
		if (!this.makeID) return;
		this.modelData = null;
		this.engineID = null;
		this.loadModels();
	}

	@Watch('modelData')
	modelChanged(): void {
		if (!this.modelData?.modelID) return;
		this.engineID = null;
		this.loadEngines();
	}

	@Watch('value')
	valueChanged(valueNew: IAutoPartsVehicle | null): void {
		this.setVehicle(valueNew);
	}

	@Watch('vehicle')
	vehicleChanged(vehicleNew: IAutoPartsVehicle | null): void {
		this.$emit('input', vehicleNew);
		if (vehicleNew) {
			this.$emit('vehicleAdded', vehicleNew);
		}
	}
}